/**
 * @file RecoverPassword.jsx
 * @description Handles password recovery and reset functionality
 *
 * @section Responsibilities
 * - Manage password recovery flow
 * - Handle email verification
 * - Process password reset
 * - Validate new passwords
 * - Manage recovery state
 * - Handle recovery errors
 *
 * @section Dependencies
 * - AuthService: Authentication handling
 * - EmailService: Verification emails
 * - ValidationService: Password validation
 * - RecoveryService: Password recovery
 *
 * @section Integration Points
 * - Auth API: Password reset
 * - Email System: Verification emails
 * - Validation System: Password rules
 * - Recovery System: Reset process
 *
 * @section State Management
 * - recoveryState: Recovery status
 * - validationState: Password validation
 * - emailState: Email verification
 * - errorState: Error handling
 *
 * @section Events
 * - onEmailSubmit: Email verification
 * - onReset: Password reset
 * - onValidation: Password validation
 * - onError: Error handling
 *
 * @section Props
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} validationRules - Password rules
 * @prop {Boolean} autoValidate - Controls validation
 * @prop {String} email - Pre-filled email
 */

import React, { useState } from "react";
import { Grid } from "@mui/material";
import DgButton from "../../components/DgButton";
import TextInput from "../../components/forms/TextInput";
import BallsBackground from "../../components/BallsBackground";
import Request from "../../utils/fetchUtils";
import Swal from "sweetalert2";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/register.css";
import { useNavigate } from "react-router";

const RecoverPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async () => {
    setLoading(true);
    const res = await Request("/user/reset-password", { email }, "POST");
    setLoading(false);
    if (res.ok) {
      setStep(2);
    } else {
      Swal.fire({
        title: "Error!",
        text: res.message || "Error requesting code",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    const res = await Request(
      "/user/reset-password",
      { email, code, password: newPassword },
      "PATCH"
    );
    setLoading(false);
    if (res.ok) {
      Swal.fire({
        title: "Success!",
        text: "Password reset successfully",
        icon: "success",
        confirmButtonText: "Ok",
      }).then(() => {
        navigate("/login");
      });
    }
  };

  const handleRequestNewCode = async () => {
    if (email) {
      const res = await Request(`/user/reset-password/code?email=${email}`);
      if (res.ok) {
        Swal.fire({
          title: "Success!",
          text: "New code requested successfully",
          icon: "success",
          confirmButtonText: "Ok",
        });
      }
    } else {
      Swal.fire({
        title: "Error!",
        text: "Please enter your email",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  return (
    <BallsBackground>
      <div className="reduced-content reduced-container shadow1">
        <div className="inner-content">
          <div className="heading">
            <h1 className="title">Recover password</h1>
          </div>
          <Grid
            container
            rowSpacing={4}
            columnSpacing={{ xs: 0, sm: 2, md: 12 }}
          >
            <Grid item xs={12}>
              <div className="form-field">
                {step === 1 && (
                  <>
                    <TextInput
                      customInput={{
                        variant: "outlined",
                        margin: "normal",
                        required: true,
                        fullWidth: true,
                        id: "email",
                        label: "Email Address",
                        name: "email",
                        autoComplete: "email",
                        autoFocus: true,
                        value: email,
                        onChange: (e) => setEmail(e.target.value),
                      }}
                    />
                    <br />
                    <br />
                    <br />
                    <DgButton
                      category={"primary"}
                      size={"large"}
                      text={"Request Code"}
                      action={handleRequestCode}
                      loading={loading}
                    />
                  </>
                )}
              </div>
            </Grid>
          </Grid>

          {step === 2 && (
            <div className="form-field">
              <TextInput
                customInput={{
                  variant: "outlined",
                  margin: "normal",
                  required: true,
                  fullWidth: true,
                  id: "code",
                  label: "Verification Code",
                  name: "code",
                  autoComplete: "code",
                  value: code,
                  onChange: (e) => setCode(e.target.value),
                }}
              />
              <br />
              <br />
              <TextInput
                customInput={{
                  variant: "outlined",
                  margin: "normal",
                  required: true,
                  fullWidth: true,
                  name: "password",
                  label: "New Password",
                  type: "password",
                  id: "password",
                  autoComplete: "new-password",
                  value: newPassword,
                  onChange: (e) => setNewPassword(e.target.value),
                }}
              />
              <br />
              <br />
              <DgButton
                category={"primary"}
                size={"large"}
                text={"Reset Password"}
                action={handleResetPassword}
                loading={loading}
              />
              {/* <br />
              <DgButton
                category={"secondary"}
                size={"large"}
                text={"Request New Code"}
                action={handleRequestNewCode}
                loading={loading}
              /> */}
            </div>
          )}

          <div
            className="sign-in-with"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/login")}
          >
            {"Go back to log in"}
          </div>
        </div>
      </div>
    </BallsBackground>
  );
};

export default RecoverPassword;
